import { db } from './db';
import type {
    Pillar,
    Tier,
    Skill,
    Drill,
    Athlete,
    TrainingPreset,
} from '@/types';

/* ─── Pillars ───────────────────────────────────────────────── */

const pillars: Pillar[] = [
    { id: 'p-takedowns', name: 'Takedowns', description: 'Attacks from neutral to score', order: 1 },
    { id: 'p-top', name: 'Top / Rides', description: 'Control and turns from top position', order: 2 },
    { id: 'p-bottom', name: 'Bottom / Escapes', description: 'Escapes, reversals, and stand-ups', order: 3 },
    { id: 'p-defense', name: 'Defense', description: 'Sprawls, hand fighting, and counter-offense', order: 4 },
    { id: 'p-conditioning', name: 'Mat Conditioning', description: 'Wrestling-specific endurance and strength', order: 5 },
];

/* ─── Tiers ──────────────────────────────────────────────────── */

function buildTiers(): Tier[] {
    const tierNames = ['Fundamentals', 'Intermediate', 'Advanced', 'Competition', 'Elite'];
    const out: Tier[] = [];
    for (const p of pillars) {
        for (let i = 0; i < tierNames.length; i++) {
            out.push({ id: `t-${p.id}-${i + 1}`, pillarId: p.id, name: tierNames[i]!, level: i + 1 });
        }
    }
    return out;
}

/* ─── Skills ─────────────────────────────────────────────────── */

const skills: Skill[] = [
    // Takedowns – Fundamentals
    { id: 's-stance', tierId: 't-p-takedowns-1', pillarId: 'p-takedowns', name: 'Proper Stance', description: 'Balanced athletic stance with good level', position: 'neutral', prerequisiteIds: [] },
    { id: 's-motion', tierId: 't-p-takedowns-1', pillarId: 'p-takedowns', name: 'Constant Motion', description: 'Circle, change direction, stay active', position: 'neutral', prerequisiteIds: ['s-stance'] },
    { id: 's-level-change', tierId: 't-p-takedowns-1', pillarId: 'p-takedowns', name: 'Level Change', description: 'Drop level with your hips, not your back', position: 'neutral', prerequisiteIds: ['s-stance'] },
    { id: 's-penetration', tierId: 't-p-takedowns-1', pillarId: 'p-takedowns', name: 'Penetration Step', description: 'Deep step to close distance for attacks', position: 'neutral', prerequisiteIds: ['s-level-change'] },
    // Takedowns – Intermediate
    { id: 's-double-leg', tierId: 't-p-takedowns-2', pillarId: 'p-takedowns', name: 'Double Leg', description: 'Drive through both legs to finish', position: 'neutral', prerequisiteIds: ['s-penetration'] },
    { id: 's-single-leg', tierId: 't-p-takedowns-2', pillarId: 'p-takedowns', name: 'Single Leg', description: 'Attack one leg and finish with a trip or lift', position: 'neutral', prerequisiteIds: ['s-penetration'] },
    { id: 's-high-crotch', tierId: 't-p-takedowns-2', pillarId: 'p-takedowns', name: 'High Crotch', description: 'Inside penetration to high crotch position', position: 'neutral', prerequisiteIds: ['s-penetration'] },
    // Takedowns – Advanced
    { id: 's-ankle-pick', tierId: 't-p-takedowns-3', pillarId: 'p-takedowns', name: 'Ankle Pick', description: 'Snap and reach for the ankle', position: 'neutral', prerequisiteIds: ['s-single-leg'] },
    { id: 's-fireman', tierId: 't-p-takedowns-3', pillarId: 'p-takedowns', name: "Fireman's Carry", description: 'Duck under and lift', position: 'neutral', prerequisiteIds: ['s-double-leg'] },

    // Top – Fundamentals
    { id: 's-base', tierId: 't-p-top-1', pillarId: 'p-top', name: 'Maintaining Base', description: 'Keep heavy hips and wide base on top', position: 'top', prerequisiteIds: [] },
    { id: 's-spiral-ride', tierId: 't-p-top-1', pillarId: 'p-top', name: 'Spiral Ride', description: 'Chop the arm and drive shoulder', position: 'top', prerequisiteIds: ['s-base'] },
    // Top – Intermediate
    { id: 's-half-nelson', tierId: 't-p-top-2', pillarId: 'p-top', name: 'Half Nelson', description: 'Classic turn from top position', position: 'top', prerequisiteIds: ['s-spiral-ride'] },
    { id: 's-arm-bar', tierId: 't-p-top-2', pillarId: 'p-top', name: 'Arm Bar', description: 'Control the arm and drive to the back', position: 'top', prerequisiteIds: ['s-base'] },
    // Top – Advanced
    { id: 's-cradle', tierId: 't-p-top-3', pillarId: 'p-top', name: 'Cradle', description: 'Lock head and knee together', position: 'top', prerequisiteIds: ['s-half-nelson'] },
    { id: 's-tilt', tierId: 't-p-top-3', pillarId: 'p-top', name: 'Tilt Series', description: 'Near-fall tilts from top', position: 'top', prerequisiteIds: ['s-arm-bar'] },

    // Bottom – Fundamentals
    { id: 's-standup', tierId: 't-p-bottom-1', pillarId: 'p-bottom', name: 'Stand Up', description: 'Base, hand control, stand and turn', position: 'bottom', prerequisiteIds: [] },
    { id: 's-sit-out', tierId: 't-p-bottom-1', pillarId: 'p-bottom', name: 'Sit Out / Turn In', description: 'Sit through to clear hips', position: 'bottom', prerequisiteIds: [] },
    // Bottom – Intermediate
    { id: 's-switch', tierId: 't-p-bottom-2', pillarId: 'p-bottom', name: 'Switch', description: 'Clear arm and hip over for reversal', position: 'bottom', prerequisiteIds: ['s-sit-out'] },
    { id: 's-granby', tierId: 't-p-bottom-2', pillarId: 'p-bottom', name: 'Granby Roll', description: 'Rolling escape to clear and score', position: 'bottom', prerequisiteIds: ['s-sit-out'] },
    // Bottom – Advanced
    { id: 's-peterson', tierId: 't-p-bottom-3', pillarId: 'p-bottom', name: 'Peterson Roll', description: 'Arm control reversal to back points', position: 'bottom', prerequisiteIds: ['s-switch'] },

    // Defense – Fundamentals
    { id: 's-sprawl', tierId: 't-p-defense-1', pillarId: 'p-defense', name: 'Sprawl', description: 'Hips back, heavy on the head', position: 'neutral', prerequisiteIds: [] },
    { id: 's-hand-fight', tierId: 't-p-defense-1', pillarId: 'p-defense', name: 'Hand Fighting', description: 'Control ties and create angles', position: 'neutral', prerequisiteIds: ['s-stance'] },
    // Defense – Intermediate
    { id: 's-whizzer', tierId: 't-p-defense-2', pillarId: 'p-defense', name: 'Whizzer / Overhook', description: 'Overhook to stop single legs', position: 'neutral', prerequisiteIds: ['s-sprawl'] },
    { id: 's-front-head', tierId: 't-p-defense-2', pillarId: 'p-defense', name: 'Front Headlock', description: 'Snap and control the head', position: 'neutral', prerequisiteIds: ['s-sprawl'] },

    // Conditioning – Fundamentals
    { id: 's-live-go', tierId: 't-p-conditioning-1', pillarId: 'p-conditioning', name: 'Live Go Rounds', description: 'Simulated match-pace sparring', position: 'neutral', prerequisiteIds: [] },
    { id: 's-shadow', tierId: 't-p-conditioning-1', pillarId: 'p-conditioning', name: 'Shadow Wrestling', description: 'Drill combos at full speed solo', position: 'neutral', prerequisiteIds: [] },
    // Conditioning – Intermediate
    { id: 's-chain', tierId: 't-p-conditioning-2', pillarId: 'p-conditioning', name: 'Chain Drilling', description: 'Link 3-4 moves in continuous reps', position: 'neutral', prerequisiteIds: ['s-shadow'] },
    { id: 's-wall-drill', tierId: 't-p-conditioning-2', pillarId: 'p-conditioning', name: 'Wall Drills', description: 'Penetration and finish against a wall', position: 'neutral', prerequisiteIds: ['s-shadow'] },

    /* ─── Youth Fundamentals (New) ────────────────────────────── */

    // Defense – Youth Fundamentals
    { id: 's-stuff-head', tierId: 't-p-defense-1', pillarId: 'p-defense', name: 'Stuffing the Head', description: 'Push the head down and away to stop an attack', position: 'neutral', prerequisiteIds: ['s-sprawl'] },
    { id: 's-headlock-counter', tierId: 't-p-defense-2', pillarId: 'p-defense', name: 'Headlock Counter Roll', description: 'Roll through a headlock to escape and score', position: 'neutral', prerequisiteIds: ['s-sprawl'] },

    // Top – Youth Fundamentals
    { id: 's-crossface', tierId: 't-p-top-1', pillarId: 'p-top', name: 'Crossface', description: 'Drive your forearm across their face to break grips and control', position: 'top', prerequisiteIds: ['s-base'] },
    { id: 's-behind-arms', tierId: 't-p-top-1', pillarId: 'p-top', name: 'Staying Behind Arms', description: 'Keep your hands and body behind their arms to maintain control', position: 'top', prerequisiteIds: ['s-base'] },
    { id: 's-flatten-pin', tierId: 't-p-top-2', pillarId: 'p-top', name: 'Flatten to Pin', description: 'Get them flat on the mat before working to pin — flatten first, pin second', position: 'top', prerequisiteIds: ['s-behind-arms', 's-crossface'] },
    { id: 's-crossface-cradle', tierId: 't-p-top-3', pillarId: 'p-top', name: 'Crossface Cradle', description: 'Use a crossface to set up and lock a cradle for the pin', position: 'top', prerequisiteIds: ['s-crossface', 's-cradle'] },

    // Bottom – Youth Fundamentals
    { id: 's-bridge-defense', tierId: 't-p-bottom-1', pillarId: 'p-bottom', name: 'Bridging Defense', description: 'Bridge up on your head and feet to avoid getting pinned', position: 'bottom', prerequisiteIds: [] },
];

/* ─── Drills ─────────────────────────────────────────────────── */

const drills: Drill[] = [
    {
        id: 'd-shadow-singles', name: 'Shadow Single Legs', position: 'neutral',
        goal: 'Build explosive penetration step muscle memory',
        cues: ['Level change before stepping', 'Head up, back straight', 'Drive through the finish'],
        commonMistakes: ['Reaching instead of stepping', 'Head down into the mat', 'Stopping at contact'],
        whenToUse: 'Warm-up or between live rounds',
        relatedSkillIds: ['s-single-leg', 's-penetration'],
    },
    {
        id: 'd-double-shots', name: 'Double Leg Blast Drill', position: 'neutral',
        goal: 'Full-speed double leg finish reps',
        cues: ['Elbows tight', 'Step between the feet', 'Turn the corner'],
        commonMistakes: ['Diving with head', 'Arms too wide', 'Not clearing the hips'],
        whenToUse: 'After warm-up, before live wrestling',
        relatedSkillIds: ['s-double-leg', 's-penetration', 's-level-change'],
    },
    {
        id: 'd-standup-wall', name: 'Stand-Up Against Wall', position: 'bottom',
        goal: 'Build stand-up base and hand control',
        cues: ['Control the hands first', 'Post hand on the wall', 'Explosive hip drive'],
        commonMistakes: ['Leaning forward', 'Not controlling hands', 'Slow to turn and face'],
        whenToUse: 'Bottom position warm-up',
        relatedSkillIds: ['s-standup'],
    },
    {
        id: 'd-sit-through', name: 'Sit-Out Turn-In Series', position: 'bottom',
        goal: 'Develop hip clearing speed from bottom',
        cues: ['Hand on the mat, leg kicks through', 'Hips high', 'Turn to face immediately'],
        commonMistakes: ['Flat hips', 'Not turning far enough', 'Losing base'],
        whenToUse: 'Bottom position reps',
        relatedSkillIds: ['s-sit-out'],
    },
    {
        id: 'd-spiral-drill', name: 'Spiral Ride Breakdowns', position: 'top',
        goal: 'Break opponent flat from top',
        cues: ['Chop the near arm', 'Drive your shoulder into their hip', 'Keep your weight forward'],
        commonMistakes: ['Reaching around without driving', 'Floating hips', 'Letting them build base'],
        whenToUse: 'Top position reps',
        relatedSkillIds: ['s-spiral-ride', 's-base'],
    },
    {
        id: 'd-half-nelson-turn', name: 'Half Nelson Turns', position: 'top',
        goal: 'Turn to back points from top',
        cues: ['Deep half nelson', 'Walk your hips into them', 'Stay tight'],
        commonMistakes: ['Arm not deep enough', 'Losing chest-to-back contact', 'Letting them hip away'],
        whenToUse: 'Top position near-fall practice',
        relatedSkillIds: ['s-half-nelson'],
    },
    {
        id: 'd-sprawl-snap', name: 'Sprawl and Snap Down', position: 'neutral',
        goal: 'React and counter-attack after sprawl',
        cues: ['Hips down hard', 'Head pressure on back of neck', 'Circle to the side'],
        commonMistakes: ['Sprawling backward instead of down', 'No follow-up', 'Staying square'],
        whenToUse: 'Defense reaction drill',
        relatedSkillIds: ['s-sprawl', 's-front-head'],
    },
    {
        id: 'd-hand-fight-circle', name: 'Hand Fight & Circle', position: 'neutral',
        goal: 'Control ties and create attacking angles',
        cues: ['Inside position wins', 'Never stop moving', 'Snap when you feel weight forward'],
        commonMistakes: ['Passive hand activity', 'Standing straight up', 'Locked in one spot'],
        whenToUse: 'Neutral warm-up every session',
        relatedSkillIds: ['s-hand-fight', 's-motion'],
    },
    {
        id: 'd-high-crotch-finish', name: 'High Crotch Finishes', position: 'neutral',
        goal: 'Finish from high crotch position',
        cues: ['Head inside', 'Trip or dump to the mat', 'Stay in good position'],
        commonMistakes: ['Head outside (guillotine risk)', 'Stalling in the position', 'Not clearing legs'],
        whenToUse: 'Intermediate attack drill',
        relatedSkillIds: ['s-high-crotch', 's-penetration'],
    },
    {
        id: 'd-switch-drill', name: 'Switch Series', position: 'bottom',
        goal: 'Hit the switch and create scoring reversal',
        cues: ['Clear the arm hard', 'Hip over fast', 'Come out on top'],
        commonMistakes: ['Not clearing the arm first', 'Rolling instead of switching', 'Ending flat'],
        whenToUse: 'Bottom escape scoring drill',
        relatedSkillIds: ['s-switch'],
    },
    {
        id: 'd-cradle-drill', name: 'Cradle Setup and Lock', position: 'top',
        goal: 'Lock up near-side and far-side cradles',
        cues: ['Head and knee must touch', 'Squeeze elbows together', 'Roll them through'],
        commonMistakes: ['Not tight enough lock', 'Losing chest contact', 'Allowing them to posture'],
        whenToUse: 'Near-fall practice after breakdown',
        relatedSkillIds: ['s-cradle', 's-half-nelson'],
    },
    {
        id: 'd-ankle-pick-setup', name: 'Ankle Pick Setups', position: 'neutral',
        goal: 'Create the snap reaction and pick the ankle',
        cues: ['Snap first, then reach', 'Off-balance them forward', 'Quick finish to mat'],
        commonMistakes: ['Reaching without snapping', 'Too far away', 'Not following to mat'],
        whenToUse: 'After mastering basic shots',
        relatedSkillIds: ['s-ankle-pick', 's-hand-fight'],
    },
    {
        id: 'd-chain-attack', name: 'Chain Wrestling Flow', position: 'neutral',
        goal: 'Link 3+ attacks in continuous sequence',
        cues: ['First attack sets up the second', 'Never stop moving', 'React to their defense'],
        commonMistakes: ['Pausing between attacks', 'One-and-done mentality', 'Losing position'],
        whenToUse: 'Advanced combination drilling',
        relatedSkillIds: ['s-chain', 's-double-leg', 's-single-leg', 's-high-crotch'],
    },
    {
        id: 'd-granby-escapes', name: 'Granby Roll Escapes', position: 'bottom',
        goal: 'Rolling escape to clear and face opponent',
        cues: ['Tuck chin', 'Roll over the shoulder', 'Come up facing'],
        commonMistakes: ['Flat roll (no shoulder roll)', 'Going the wrong direction', 'Ending on back'],
        whenToUse: 'Bottom escape variety drill',
        relatedSkillIds: ['s-granby'],
    },
    {
        id: 'd-whizzer-counter', name: 'Whizzer Counter Series', position: 'neutral',
        goal: 'Defend single legs and counter-score',
        cues: ['Deep overhook', 'Hip into them', 'Look for the throw-by or pancake'],
        commonMistakes: ['Weak overhook', 'Backing up instead of driving', 'No counter-attack'],
        whenToUse: 'Defensive counter practice',
        relatedSkillIds: ['s-whizzer', 's-sprawl'],
    },
    {
        id: 'd-tilt-series', name: 'Tilt Series from Top', position: 'top',
        goal: 'Score near-fall with controlled tilts',
        cues: ['Arm control first', 'Walk hips to the side', 'Expose the back slowly'],
        commonMistakes: ['Pulling too hard', 'Losing arm control', 'Going too fast'],
        whenToUse: 'Top position scoring',
        relatedSkillIds: ['s-tilt', 's-arm-bar'],
    },
    {
        id: 'd-motion-drill', name: 'Footwork & Motion Drill', position: 'neutral',
        goal: 'Build constant mat movement habit',
        cues: ['Never cross feet', 'Stay in stance', 'Change direction every 3 steps'],
        commonMistakes: ['Crossing feet', 'Standing upright', 'Predictable pattern'],
        whenToUse: 'Every warm-up session',
        relatedSkillIds: ['s-motion', 's-stance'],
    },
    {
        id: 'd-live-go-protocol', name: 'Live Go Protocol', position: 'neutral',
        goal: 'Simulate match intensity in practice',
        cues: ['Full speed, full contact', 'Reset after out of bounds', 'Score like a match'],
        commonMistakes: ['Holding back intensity', 'No resets, just rolling', 'Not keeping score'],
        whenToUse: 'Last 15–20 min of every practice',
        relatedSkillIds: ['s-live-go'],
    },
    {
        id: 'd-wall-shots', name: 'Wall Shots', position: 'neutral',
        goal: 'Build explosive penetration without a partner',
        cues: ['Level change, step, drive', 'Hands finish on the wall', 'Return to stance fast'],
        commonMistakes: ['Not changing level', 'Falling into the wall', 'Slow recovery'],
        whenToUse: 'Solo training, conditioning finisher',
        relatedSkillIds: ['s-wall-drill', 's-penetration'],
    },
    {
        id: 'd-shadow-wrestling', name: 'Shadow Wrestling Rounds', position: 'neutral',
        goal: 'Full-speed technique reps without partner',
        cues: ['Visualize an opponent', 'Mix attacks and defenses', 'Keep match pace'],
        commonMistakes: ['Going through the motions', 'No intensity', 'Only offense'],
        whenToUse: 'Warm-up or solo training',
        relatedSkillIds: ['s-shadow', 's-motion'],
    },

    /* ─── Youth Fundamental Drills (New) ──────────────────────── */

    {
        id: 'd-stuff-head', name: 'Head Stuffing Drill', position: 'neutral',
        goal: 'Learn to defend takedowns by stuffing the head down',
        cues: ['Hands on top of their head', 'Push down and back', 'Stay in your stance'],
        commonMistakes: ['Reaching too far forward', 'Bending at the waist', 'Feet stop moving'],
        whenToUse: 'Takedown defense practice',
        relatedSkillIds: ['s-stuff-head', 's-sprawl'],
    },
    {
        id: 'd-behind-arms', name: 'Behind the Arms Control', position: 'top',
        goal: 'Stay behind their arms on top so they can\'t escape',
        cues: ['Hands stay behind their elbows', 'Chest stays tight to their back', 'Move with them'],
        commonMistakes: ['Reaching over their shoulders', 'Letting them get wrist control', 'Floating hips off'],
        whenToUse: 'Top position control drill',
        relatedSkillIds: ['s-behind-arms', 's-base'],
    },
    {
        id: 'd-crossface-break', name: 'Crossface Grip Breaking', position: 'top',
        goal: 'Use a crossface to break grips and turn their head',
        cues: ['Forearm across the face, not the throat', 'Drive through to break their grip', 'Follow up with control'],
        commonMistakes: ['Hitting the throat instead of the face', 'Not driving through', 'Losing your position after the crossface'],
        whenToUse: 'When opponent is gripping and blocking on bottom',
        relatedSkillIds: ['s-crossface', 's-behind-arms'],
    },
    {
        id: 'd-headlock-counter', name: 'Headlock Counter Roll', position: 'neutral',
        goal: 'Escape a headlock and roll through to score',
        cues: ['Lock your arms around their waist', 'Step your far leg behind them', 'Roll through and end on top'],
        commonMistakes: ['Panicking and pulling away', 'Not locking the waist', 'Rolling the wrong direction'],
        whenToUse: 'When caught in a headlock during a match',
        relatedSkillIds: ['s-headlock-counter', 's-sprawl'],
    },
    {
        id: 'd-flatten-pin', name: 'Flatten and Pin Setup', position: 'top',
        goal: 'Get opponent flat on the mat before working the pin',
        cues: ['Drive your hips into them', 'Walk your feet out to flatten them', 'Once flat, work the half nelson or arm bar'],
        commonMistakes: ['Trying to pin before they are flat', 'Hips too high', 'Letting them get back to base'],
        whenToUse: 'When on top and opponent is on hands and knees',
        relatedSkillIds: ['s-flatten-pin', 's-half-nelson'],
    },
    {
        id: 'd-headlock-recovery', name: 'Headlock Position Recovery', position: 'neutral',
        goal: 'Recognize when you\'re caught in a headlock and get safe',
        cues: ['Stay calm — don\'t panic', 'Get your hips underneath you', 'Lock their waist before they throw'],
        commonMistakes: ['Trying to pull the head out', 'Going to your back', 'Not getting hips under fast enough'],
        whenToUse: 'Headlock defense situational drill',
        relatedSkillIds: ['s-headlock-counter'],
    },
    {
        id: 'd-bridge-drill', name: 'Bridging Technique', position: 'bottom',
        goal: 'Build a strong bridge to get off your back',
        cues: ['Head and feet on the mat', 'Push your hips to the ceiling', 'Turn to your belly as fast as you can'],
        commonMistakes: ['Flat bridge — not getting hips high', 'Not turning to belly after bridging', 'Giving up and staying on back'],
        whenToUse: 'When on your back and in danger of getting pinned',
        relatedSkillIds: ['s-bridge-defense'],
    },
    {
        id: 'd-chopping-feet', name: 'Chopping Feet Forward', position: 'neutral',
        goal: 'Keep your feet moving forward with short choppy steps',
        cues: ['Small fast steps, never cross your feet', 'Always moving forward', 'Stay in your stance the whole time'],
        commonMistakes: ['Taking big steps', 'Crossing feet', 'Standing straight up while moving'],
        whenToUse: 'Every warm-up — all kids, every practice',
        relatedSkillIds: ['s-motion', 's-penetration'],
    },
    {
        id: 'd-airplane', name: 'Airplane Circle Drill', position: 'neutral',
        goal: 'Move around your opponent without using your hands',
        cues: ['Hands behind your back', 'Circle around your partner', 'Stay low and in your stance'],
        commonMistakes: ['Standing up tall', 'Moving too fast and losing balance', 'Not changing direction'],
        whenToUse: 'Footwork and balance warm-up — all kids',
        relatedSkillIds: ['s-motion', 's-stance'],
    },
    {
        id: 'd-crossface-cradle', name: 'Crossface Cradle Setup & Finish', position: 'top',
        goal: 'Use a crossface to set up the cradle and lock it for the pin',
        cues: ['Crossface first to turn the head', 'Lock head and knee together', 'Squeeze tight and roll through'],
        commonMistakes: ['Skipping the crossface and just grabbing', 'Lock not tight enough', 'Losing chest contact during the roll'],
        whenToUse: 'Top position near-fall and pin practice',
        relatedSkillIds: ['s-crossface-cradle', 's-crossface', 's-cradle'],
    },
];

/* ─── Athletes ───────────────────────────────────────────────── */

const athletes: Athlete[] = [
    { id: 'ath-1', name: 'Demo Wrestler', level: 'Beginner', experienceYears: 0, avatar: '🤼', createdAt: new Date().toISOString() },
    { id: 'ath-2', name: 'Training Partner', level: 'Intermediate', experienceYears: 2, avatar: '🦍', createdAt: new Date().toISOString() },
];

/* ─── Training Presets ───────────────────────────────────────── */

const presets: TrainingPreset[] = [
    { id: 'pre-quick5', name: 'Quick 5', position: 'neutral', workSeconds: 30, restSeconds: 15, rounds: 5, cueMode: 'basic' },
    { id: 'pre-drilling', name: 'Drill Session', position: 'neutral', workSeconds: 60, restSeconds: 30, rounds: 6, cueMode: 'basic' },
    { id: 'pre-live-go', name: 'Live Go', position: 'neutral', workSeconds: 120, restSeconds: 60, rounds: 5, cueMode: 'none' },
    { id: 'pre-top-work', name: 'Top Work', position: 'top', workSeconds: 45, restSeconds: 20, rounds: 6, cueMode: 'basic' },
    { id: 'pre-bottom-esc', name: 'Bottom Escapes', position: 'bottom', workSeconds: 30, restSeconds: 15, rounds: 8, cueMode: 'basic' },
    { id: 'pre-endurance', name: 'Endurance Builder', position: 'neutral', workSeconds: 90, restSeconds: 30, rounds: 8, cueMode: 'none' },
];

/* ─── Seed Function ──────────────────────────────────────────── */

export async function seedDatabase(): Promise<void> {
    // 1. Check if ANY seeding has happened
    const settings = await db.appSettings.get(1);
    const presetsCount = await db.trainingPresets.count();

    // If fully seeded and presets exist, we're good
    if (settings?.seeded && presetsCount > 0) return;

    console.log('[Palaestra] Starting database seed...');

    // 2. Prepare data
    const tiers = buildTiers();

    await db.transaction('rw', [
        db.pillars, db.tiers, db.skills, db.drills,
        db.athletes, db.trainingPresets, db.appSettings,
    ], async () => {
        // Only seed core data if not already seeded
        if (!settings?.seeded) {
            await db.pillars.bulkPut(pillars);
            await db.tiers.bulkPut(tiers);
            await db.skills.bulkPut(skills);
            await db.drills.bulkPut(drills);
            await db.athletes.bulkPut(athletes);

            await db.appSettings.put({
                id: 1,
                audioMode: 'on',
                offlineMode: 'auto',
                seeded: true,
                activeAthleteId: 'ath-1',
                onboardingCompleted: false,
            });
            console.log('[Palaestra] Core data seeded');
        }

        // Always ensure presets exist if missing
        if (presetsCount === 0) {
            await db.trainingPresets.bulkPut(presets);
            console.log('[Palaestra] Training presets seeded');
        }
    });

    // Handle existing users: if seeded but no onboardingCompleted, auto-complete
    const updated = await db.appSettings.get(1);
    if (updated && updated.seeded && updated.onboardingCompleted === undefined) {
        await db.appSettings.update(1, { onboardingCompleted: true });
        console.log('[Palaestra] Existing user: auto-completed onboarding');
    }

    console.log('[Palaestra] Database ready ✓');
}
